import {
  Box,
  Icon,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import moment from 'moment';
import { AiOutlineEdit } from 'react-icons/ai';
import { OwnBookingsBookingsData } from '../../../types/AdminTypes';
import { Link as ScrollLink } from 'react-scroll';
import { ObjectsParams } from '../../../types/ParamsTypes';
import styled from '@emotion/styled';
import { ImFileEmpty } from 'react-icons/all';
import React, { useMemo } from 'react';
import { DeleteModal } from './DeleteModal';
import { BookingEditParams } from '../../../types/BookingTypes';
import { clientBookingText } from '../../../services/Admin/DashboardService';

export const TableWrapper = styled.div`
  td {
    padding: 10px 0 10px 0;

    &:last-child {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  @media (max-width: 767px) {
    table,
    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    tr {
      border-bottom: 2px solid var(--chakra-colors-gray-500);
    }

    td {
      border: none;
      border-bottom: 1px solid #eee;
      position: relative;
      padding: 10px 15px 10px 50%;
      min-height: 45px;
    }

    td:before {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 8px;
      width: 45%;
      padding-right: 32px;
      white-space: nowrap;
      color: var(--chakra-colors-gray-900);
      font-weight: 600;
    }

    td:nth-of-type(1):before {
      content: 'ANREISE';
    }

    td:nth-of-type(2):before {
      content: 'ABREISE';
    }

    td:nth-of-type(3):before {
      content: 'KOMMENTAR';
    }

    td:nth-of-type(4):before {
      content: 'AKTIONEN';
    }
  }
`;

interface TableBookingsProps {
  objectSelected: OwnBookingsBookingsData;
  objectsParams: ObjectsParams;
  handleDateRange: (updatedObjectsParams: ObjectsParams) => void;
  handleDirtyObjects: (value: boolean) => void;
  handleClose?: () => void;
  handleTokenState: (value: string | null) => void;
  handleBookingEditParams: (values: BookingEditParams) => void;
}

const OWN_BOOKING = 'OWN_BOOKING';
const BOOKING = 'BOOKING';

export const TableBookings = (props: TableBookingsProps): JSX.Element => {
  const {
    objectSelected,
    objectsParams,
    handleDateRange,
    handleDirtyObjects,
    handleClose,
    handleTokenState,
    handleBookingEditParams,
  } = props;
  const bookingsFiltered = useMemo(() => {
    return objectSelected.Bookings.filter(booking => {
      return booking.Type === OWN_BOOKING || booking.Type === BOOKING;
    });
  }, [objectSelected]);

  const [isTabletOrMobile] = useMediaQuery('(max-width: 767px)');

  return (
    <Box w={'100%'} h={{ base: '100%', md: 'unset' }} overflow={'auto'}>
      <VStack gridGap={4}>
        <Text w={'100%'} textAlign={'left'} fontSize={'xl'} textStyle={'category.primary'}>
          Belegungsliste
        </Text>
        <Box w={'100%'}>
          {!bookingsFiltered.length && (
            <VStack bg={'white'} p={4} borderRadius={{ base: 0, md: 4 }} width={'100%'}>
              <Icon as={ImFileEmpty} fontSize={'4xl'} color={'gray.500'} />
              <Text fontSize={'md'} color={'gray.500'}>
                Keine eigenen Buchungen für diesen Zeitraum vorhanden
              </Text>
            </VStack>
          )}
          {bookingsFiltered.length ? (
            <TableWrapper>
              <Table size={'lg'}>
                <Thead>
                  <Tr>
                    <Th pl={0}>Anreise</Th>
                    <Th isNumeric={false}>Abreise</Th>
                    <Th isNumeric={false}>Kommentar</Th>
                    <Th pr={0} isNumeric={true}>
                      Aktionen
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {bookingsFiltered.map((booking, idx) => {
                    return (
                      <Tr key={idx}>
                        <Td pl={0} color={'gray.800'} isNumeric={false}>
                          {moment(booking.arrivalDate).format('DD.MM.YYYY')}
                        </Td>
                        <Td color={'gray.800'} isNumeric={false}>
                          {moment(booking.departureDate).format('DD.MM.YYYY')}
                        </Td>
                        <Td color={'gray.800'} isNumeric={false}>
                          {booking.Type === BOOKING && clientBookingText(booking, 'COMMENT')}
                          {booking.Type === OWN_BOOKING && booking.Comment}
                          {/*{booking.Type === BOOKING && '-'}*/}
                        </Td>
                        <Td pr={8} isNumeric={!isTabletOrMobile} color={'gray.800'}>
                          {booking.Type === OWN_BOOKING && (
                            <>
                              <ScrollLink
                                to="bookingForm"
                                spy={true}
                                smooth={true}
                                duration={500}
                                offset={-50}>
                                <IconButton
                                  icon={<AiOutlineEdit />}
                                  aria-label={'edit booking'}
                                  variant="ghost"
                                  colorScheme={'blue'}
                                  mr={4}
                                  onClick={() => {
                                    handleClose && handleClose();
                                    handleDateRange({
                                      ...objectsParams,
                                      fromDate: booking.arrivalDate,
                                      toDate: booking.departureDate,
                                    });
                                    handleBookingEditParams({
                                      booking: booking.uniqueId,
                                      BookingComment: booking.Comment,
                                      initialMonth: new Date(booking.departureDate)
                                    });
                                  }}
                                />
                              </ScrollLink>
                              <DeleteModal
                                objectSelected={objectSelected}
                                bookingToDelete={booking}
                                handleDirtyObjects={handleDirtyObjects}
                                handleTokenState={handleTokenState}
                                objectsParams={objectsParams}
                                handleDateRange={handleDateRange}
                                handleBookingEditParams={handleBookingEditParams}
                              />
                            </>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableWrapper>
          ) : null}
        </Box>
      </VStack>
    </Box>
  );
};
